import { useState } from 'react'
import type Bluebird from 'bluebird'
import useDeepEffect from 'legacy/common/react/hooks/useDeepEffect'
import { usePartnerId } from 'utils/routing/params.hooks'
import {
  dataFetchingLoading,
  type DataFetchingState,
  dataFetchingSuccess,
  dataFetchingError,
} from 'legacy/common/utils/data-fetching/dataFetching.types'
import {
  usePerformanceMetricsQueryParams,
  type UsePerformanceMetricsQueryParamsProps,
} from 'legacy/sections/analytics/components/performance-metrics/utils/performanceMetricsQueryParams.hooks'
import {
  performanceMetricsApiFetchData,
  PERFORMANCE_METRICS_API_DEFAULT_DATE_RANGE,
} from 'legacy/sections/analytics/components/performance-metrics/utils/performanceMetrics.api'
import { type PerformanceMetricsApiResponse } from 'legacy/sections/analytics/components/performance-metrics/utils/performanceMetrics.types'

interface UsePerformanceMetricsBehaviourProps extends UsePerformanceMetricsQueryParamsProps {}

const initialApiState = dataFetchingLoading<PerformanceMetricsApiResponse>()

export const usePerformanceMetricsBehaviour = (props: UsePerformanceMetricsBehaviourProps) => {
  const [pendingRequest, setPendingRequest] = useState<Bluebird<void> | undefined>()
  const partnerId = usePartnerId()

  const {
    retailerId,
    setRetailerId,
    orderType,
    setOrderType,
    orderSource,
    setOrderSource,
    dateSelectRangeValue,
    setDateSelectRangeValue,
    retailerLocationSelectValue,
    setRetailerLocationSelectValue,
  } = usePerformanceMetricsQueryParams(props)

  const apiParams = {
    partnerId,
    retailerId,
    orderType,
    orderSource,
    dateSelectRangeValue,
    retailerLocationSelectValue,
  }

  const [performanceMetricsApiState, setPerformanceMetricsApiState] =
    useState<DataFetchingState<PerformanceMetricsApiResponse>>(initialApiState)

  useDeepEffect(() => {
    // If we have an in-flight request, cancel it
    if (pendingRequest) pendingRequest.cancel()

    setPerformanceMetricsApiState(dataFetchingLoading())

    const newPendingRequest = performanceMetricsApiFetchData(apiParams)
      .then(result => setPerformanceMetricsApiState(dataFetchingSuccess(result)))
      .catch((err?: Error) => setPerformanceMetricsApiState(dataFetchingError(err)))

    // Store the request for cancellation if needed
    setPendingRequest(newPendingRequest)

    // Cancel any pending requests on unmount
    return () => {
      if (pendingRequest) pendingRequest.cancel()
    }
  }, [apiParams])

  return {
    retailerId,
    setRetailerId,
    orderType,
    setOrderType,
    orderSource,
    setOrderSource,
    defaultDateRange: PERFORMANCE_METRICS_API_DEFAULT_DATE_RANGE,
    dateSelectRangeValue,
    setDateSelectRangeValue,
    retailerLocationSelectValue,
    setRetailerLocationSelectValue,
    error: performanceMetricsApiState.error,
    loading: performanceMetricsApiState.loading,
    performanceMetricsData:
      (performanceMetricsApiState.data && performanceMetricsApiState.data.data) || [],
  }
}

export type PerformanceMetricsBehaviour = ReturnType<typeof usePerformanceMetricsBehaviour>
